'use client'

import { AdvertisingModalEmbedded } from '@/AdvertisingModal/AdvertisingModalEmbedded'
import {
  FREEMAGIC_REGISTERED_FREE_DOWNLOAD_EVENT,
  freemagicFreeDownloadNames,
} from '@/freemagicTracking'
import { EMAIL_REGEX } from '@/validation'

const COURSE_THUMB =
  'https://vinc.gumlet.io/gallery/video-thumbnails/10x10.jpg?nocache=256559665'

/** Explicit fields so name + email always render; labels + placeholders when `hideLabels` is false. */
const formFieldsSignup = [
  {
    name: 'name',
    label: 'Name',
    type: 'text' as const,
    placeholder: 'First and last name',
    required: true,
    validation: {
      pattern: /^[a-zA-Z]+\s+[a-zA-Z]+/,
      message: 'Please enter your first and last name',
    },
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email' as const,
    placeholder: 'your@email.com',
    required: true,
    validation: {
      pattern: EMAIL_REGEX,
      message: 'Please enter a valid email address',
    },
  },
]

const shared = {
  imageUrl: COURSE_THUMB,
  imageAlt: 'Paul Wilson — free card magic video course',
  submitButtonText: 'Start my free video now',
  successMessage:
    "You're in. Check your email—then dive into the rest of the free library below.",
  formFields: formFieldsSignup,
}

/** Tighter copy for full-width layout if used elsewhere. */
const contentFullWidth = {
  ...shared,
  title: 'Learn the best card magic. Free',
  introHtml: `<p>Legend of magic, crooked-gambling expert, film director—star of <em>The Real Hustle</em> and countless TV spots—now your teacher. One free download: a full intro to close-up card magic.</p><p><strong>10 lessons · 10 principles · 10 tricks</strong> with an ordinary deck. Shuffles, secret card picks, palming, and more—<strong>zero cost</strong>.</p><p>Create your free Vanishing Inc. account below and watch immediately.</p>`,
}

/** Sidebar: minimal copy—the headline does the work. */
const contentSidebar = {
  ...shared,
  title: 'Learn the best card magic. Free',
  introHtml: `<p>10 killer tricks. Zero cost. Sign up below and watch instantly.</p>`,
}

const contentArticle = {
  ...shared,
  title: 'Get the free beginner card magic course',
  introHtml: `<p>Learn 10 tricks and 10 fundamentals from R. Paul Wilson with an ordinary deck of cards.</p>`,
}

const configFullWidth = {
  layout: 'side-by-side' as const,
  hideForLoggedInUsers: false,
  showFormForLoggedInUsers: true,
}

const configArticle = {
  layout: 'side-by-side' as const,
  hideForLoggedInUsers: false,
  showFormForLoggedInUsers: true,
}

const configSidebar = {
  layout: 'stacked' as const,
  hideForLoggedInUsers: true,
  showFormForLoggedInUsers: true,
}

type FreeMagicDownloadsSignupModalProps = {
  variant?: 'sidebar' | 'fullWidth' | 'article'
}

export function FreeMagicDownloadsSignupModal({
  variant = 'fullWidth',
}: FreeMagicDownloadsSignupModalProps) {
  const isSidebar = variant === 'sidebar'
  const isArticle = variant === 'article'
  const content = isSidebar
    ? contentSidebar
    : isArticle
      ? contentArticle
      : contentFullWidth
  const config = isSidebar
    ? configSidebar
    : isArticle
      ? configArticle
      : configFullWidth
  const className = isSidebar
    ? '!mb-0 w-full max-w-none !overflow-visible border-gray-200 shadow-xl'
    : isArticle
      ? '!mb-0 w-full max-w-none overflow-hidden border-gray-200 shadow-sm'
      : '!mb-0 w-full max-w-none overflow-hidden border-gray-200 shadow-xl'
  const formClassName = isSidebar
    ? 'space-y-4'
    : isArticle
      ? 'space-y-5'
      : 'space-y-6'
  const customInputClassName = isSidebar
    ? 'w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30'
    : isArticle
      ? 'w-full rounded-sm border border-gray-300 px-4 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500'
      : 'w-full rounded-lg border border-gray-300 px-4 py-3 text-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500'
  const customButtonClassName = isSidebar
    ? 'w-full rounded-xl py-3.5 text-base font-semibold shadow-md'
    : isArticle
      ? 'w-full rounded-sm py-3 text-base font-semibold'
      : 'w-full py-3 text-lg'
  const customTitleClassName = isSidebar
    ? 'mb-2 text-left font-archivo text-2xl font-black leading-[1.15] tracking-tight text-gray-950 sm:text-3xl'
    : isArticle
      ? 'text-left font-archivo text-2xl font-black leading-tight text-gray-950 sm:text-3xl'
      : 'text-left font-archivo text-2xl font-black leading-tight text-gray-900 sm:text-3xl'
  const customIntroClassName = isSidebar
    ? 'text-left text-base leading-snug text-gray-800 sm:text-lg [&_p]:mb-0'
    : isArticle
      ? 'text-left text-base leading-7 text-gray-700 [&_p]:mb-0'
      : 'space-y-3 text-left text-base leading-relaxed text-gray-700 [&_p]:mb-0'
  const customSuccessClassName = isSidebar
    ? 'space-y-3 py-10 text-center'
    : isArticle
      ? 'py-6 text-center'
      : 'py-6 text-center'
  const customSuccessTitleClassName = isSidebar
    ? 'text-lg font-bold text-green-800'
    : isArticle
      ? 'mb-2 text-base font-semibold text-green-700'
      : 'mb-2 text-base font-semibold text-green-700'
  const customSuccessMessageClassName = isSidebar
    ? 'text-base leading-relaxed text-green-800'
    : isArticle
      ? 'text-sm text-green-700'
      : 'text-sm text-green-700'

  return (
    <section
      aria-label="Paul Wilson free card magic video signup"
      className={isSidebar ? 'w-full' : undefined}
    >
      <AdvertisingModalEmbedded
        content={content}
        trackingEventName={FREEMAGIC_REGISTERED_FREE_DOWNLOAD_EVENT}
        trackingProperties={{
          'Free download name': freemagicFreeDownloadNames.cardMagicCourse,
        }}
        config={config}
        stackedContentClassName={
          isSidebar ? 'px-6 pb-8 pt-4 md:px-8 md:pb-10 md:pt-5' : undefined
        }
        formClassName={formClassName}
        className={className}
        customInputClassName={customInputClassName}
        customButtonClassName={customButtonClassName}
        customTitleClassName={customTitleClassName}
        customIntroClassName={customIntroClassName}
        customSuccessClassName={customSuccessClassName}
        customSuccessTitleClassName={customSuccessTitleClassName}
        customSuccessMessageClassName={customSuccessMessageClassName}
        hideTitleInSuccess={true}
        hideLabels={false}
        hideErrorBox={false}
      />
    </section>
  )
}
